import React, { useEffect, useState } from "react";
import { registerView, ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Col, Jumbotron, Row } from "react-bootstrap";
import Button from "@edaz/edazkit/Button";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import PaymentRetryViewModel from "viewModels/common/payment/paymentRetryViewModel";
import { getTimeZoneLabel } from "utils/dateUtils";
import Tooltip from "controls/tooltip";
import { isMobile } from "react-device-detect";
import LoadingModal from "../loadingModal/loadingModal";
import IdleModal from "views/common/idleModal/idleModal";
import { useIdleTimer } from "react-idle-timer";
import formatMillisecondsToTime from "utils/formatMilisecondsToTime";
import { EDAZGooglePayButton } from "views/eshop/order/components/googlePayButton";
import toolboxStyles from "views/eshop/order/components/toolboxView.module.scss";

const SingleRecap: ViewComponent<PaymentRetryViewModel> = observer(({ vm }) => {
  const tg = (code: string) => vm.localizationService.translateGeneral(`eshop.order.summary.${code}`);
  const tr = (code: string) => vm.localizationService.translateGeneral(`eshop.payment.retry.${code}`);

  const renderDateWithTimeZone = (date: Date) => {
    const formatted = vm.localizationService.formatDateTime(date);
    return !vm.isDifferentTimeZone ? (
      <span>{formatted}</span>
    ) : (
      <>
        <span>{formatted}</span>
        <Tooltip
          id="cet-tooltip"
          placement={isMobile ? "bottom" : "right"}
          tooltip={vm.localizationService.translateGeneral("timezoneTooltip")}
          trigger={["hover", "focus"]}>
          <span style={{ marginLeft: 4, textDecoration: "underline", textDecorationStyle: "dotted" }}>
            {getTimeZoneLabel(date)}
          </span>
        </Tooltip>
      </>
    );
  };

  return (
    <>
      <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <span>{tg("vehicle")}</span>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <span className="font-weight-bold text-nowrap">{vm.vehicleInfo}</span>
        </Col>
      </Row>
      <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <span>{tg("markType")}</span>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <span className="font-weight-bold text-nowrap">{vm.chargeType}</span>
        </Col>
      </Row>
      <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <span>{tg("fuelType")}</span>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <span className="font-weight-bold text-nowrap">{vm.fuelType}</span>
        </Col>
      </Row>
      <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <span>{tg("validSince")}</span>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <span className="font-weight-bold text-nowrap">
            {vm.validSince ? renderDateWithTimeZone(new Date(vm.validSince)) : ""}
          </span>
        </Col>
      </Row>
      <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <span>{tg("validUntil")}</span>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <span className="font-weight-bold text-nowrap">
            {vm.validUntil ? renderDateWithTimeZone(new Date(vm.validUntil)) : ""}
          </span>
        </Col>
      </Row>
      <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <span>{tr("emailPayment")}</span>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <span className="font-weight-bold text-nowrap">{vm.email}</span>
        </Col>
      </Row>

      {!vm.isSingleDay && (
        <>
          <div className="mx-0 mt-3 mb-3 border-top" />

          <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
            <Col lg="auto" className="m-0 p-0">
              <span>{tr("emailNotification")}</span>
            </Col>
            <Col lg="auto" className="m-0 p-0">
              <span className="font-weight-bold text-nowrap">{vm.notificationEmail || tr("notProvided")}</span>
            </Col>
          </Row>
          <Row className="d-flex justify-content-between mr-0 mb-3 ml-0">
            <Col lg="auto" className="m-0 p-0">
              <span>{tr("phoneNotification")}</span>
            </Col>
            <Col lg="auto" className="m-0 p-0">
              <span className="font-weight-bold text-nowrap">{vm.notificationPhone || tr("notProvided")}</span>
            </Col>
          </Row>
        </>
      )}

      <div className="mx-0 mt-3 mb-4 border-top" />

      <Row className="d-flex justify-content-between mr-0 mb-4 ml-0">
        <Col lg="auto" className="m-0 p-0">
          <h5 className="mb-0">{tr("price")}</h5>
        </Col>
        <Col lg="auto" className="m-0 p-0">
          <h5 className="mb-0">{vm.singlePrice}</h5>
        </Col>
      </Row>
    </>
  );
});

const MultipleRecap: ViewComponent<PaymentRetryViewModel> = observer(({ vm }) => {
  const tr = (code: string) => vm.localizationService.translateGeneral(`eshop.payment.retry.${code}`);

  const charges = vm.uniqueCharges();

  return (
    <>
      {charges.map(charge => (
        <Row key={charge.index} className="d-flex justify-content-between mr-0 mb-3 ml-0">
          <Col xs="auto" className="m-0 p-0">
            <span className="font-weight-bold">{vm.vehicleCountLabel(charge.index)}</span>
          </Col>
          <Col xs="auto" className="m-0 p-0 text-right">
            <div className="text-muted">
              {vm.chargeTypeLabel(charge.chargeTypeId)}, {vm.pluralizeVehicles(charge.count)}
            </div>
            <span className="font-weight-bold">{vm.chargeTypeTotal(charge.chargeTypeId, charge.fuelTypeId, charge.count)}</span>
          </Col>
        </Row>
      ))}

      <div className="mx-0 mt-3 mb-4 border-top" />

      <Row className="d-flex justify-content-between mr-0 mb-4 ml-0">
        <Col xs="auto" className="m-0 p-0">
          <h5 className="mb-0">{tr("totalToPay")}</h5>
        </Col>
        <Col xs="auto" className="m-0 p-0">
          <h5 className="mb-0">{vm.grandTotal}</h5>
        </Col>
      </Row>
    </>
  );
});

const PaymentRetryView: ViewComponent<PaymentRetryViewModel> = observer(({ vm }) => {
  const tr = (code: string) => vm.localizationService.translateGeneral(`eshop.payment.retry.${code}`);
  const tg = (code: string) => vm.localizationService.translateGeneral(`eshop.${code}`);
  const tge = (code: string) => vm.localizationService.translateGeneral(`eshop.order.${code}`);
  const modalTranslate = (code: string) => vm.localizationService.translateGeneral(`eshop.card.${code}`);
  const idleModalTranslate = (code: string) => vm.localizationService.translateGeneral(`eshop.idle.${code}`);

  const { getRemainingTime } = useIdleTimer({
    onIdle: vm.handleOnIdle,
    timeout: vm.IDLE_TIMER_TIMEOUT,
    debounce: vm.IDLE_TIMER_DEBOUNCE,
    crossTab: true,
    eventsThrottle: 200,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
    ],
  });

  const [timeLeft, setTimeLeft] = useState(vm.IDLE_TIMER_TIMEOUT + vm.IDLE_PROMPT_TIMEOUT);

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = getRemainingTime();
      const calculatedTimeLeft = Math.ceil(remaining / 1000) * 1000 + vm.IDLE_PROMPT_TIMEOUT;
      setTimeLeft(calculatedTimeLeft);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const backToPurchaseLabel =
    vm.localizationService.translateGeneral("eshop.payment.status.failure.messages.any.any.link") ||
    tg("payment.retry.backToPurchase");

  let timer;
  if (vm.IDLE_TIMER_TIMEOUT + vm.IDLE_PROMPT_TIMEOUT - timeLeft >= vm.IDLE_TIMER_DELAY && getRemainingTime() > 0) {
    timer = (
      <div className="d-flex flex-grow-1 justify-content-end order-timer-wrapper">
        <Tooltip id="retry-timer-tooltip" placement="bottom-end" tooltip={tge("timerTooltip")} show={true}>
          <div className="order-timer">
            <span>{formatMillisecondsToTime(timeLeft)}</span>
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="mx-auto p-0 p-sm-3" style={{ maxWidth: "780px", width: "100%" }}>
      {timer && <div className="d-flex justify-content-end mb-2">{timer}</div>}
      <Jumbotron className="d-flex justify-content-center align-items-center card-box-shadow mb-3 p-4 p-md-5 text-center flex-column">
        <Icon icon={IconType.CircleError} width={64} height={64} className="mb-3 text-danger" />
        <h5 className="mb-2">{tr("title")}</h5>
        <div className="mb-5 px-3">{tr("description")}</div>

        <div className="w-100 text-left">{vm.isMultiple ? <MultipleRecap vm={vm} /> : <SingleRecap vm={vm} />}</div>

        {vm.retryBlocked ? (
          <Button
            type="button"
            variant="primary"
            className="btn-block w-100 mt-3 mb-0"
            onClick={vm.handleBackToPurchase}
            disabled={vm.isLoading}>
            {backToPurchaseLabel.toUpperCase()}
          </Button>
        ) : (
          <div
            className={`w-100 mt-3 d-flex ${
              vm.isGooglePayAllowed && vm.isCardPayAllowed ? "flex-column flex-md-row align-items-stretch" : "flex-column"
            }`}
            style={{ gap: "0.75rem", minHeight: 48 }}>
            {vm.isCardPayAllowed && (
              <Button
                type="button"
                variant="primary"
                className="btn-block w-100 flex-grow-1 flex-basis-0 mt-0 mb-0"
                onClick={vm.handleRetryPayment}
                disabled={vm.isLoading}
                style={{ minHeight: 48, height: 48, flexBasis: 0 }}>
                {tr("payByCard").toUpperCase()}
              </Button>
            )}
            {vm.isGooglePayAllowed && (
              <div className="d-flex flex-grow-1" style={{ minHeight: 48, height: 48, flexBasis: 0 }}>
                <EDAZGooglePayButton
                  totalPrice={vm.grandTotalRawForGooglePay}
                  locale={vm.localizationService.locale}
                  googlePayMetadata={vm.googlePayMetadata}
                  loading={!vm.isGooglePayMetadataReady}
                  createOrder={async (_errorMessage: unknown, paymentData?: google.payments.api.PaymentData) => {
                    if (paymentData) {
                      await vm.handleRetryGooglePayPayment(paymentData);
                    }
                  }}
                  validateOrder={vm.validateForGooglePay}
                  errorMessage={vm.localizationService.translateGeneral("errors.generic")}
                  fillContainer={vm.isCardPayAllowed}
                />
              </div>
            )}
          </div>
        )}
      </Jumbotron>
      <LoadingModal translate={modalTranslate} isVisible={vm.isLoading} />
      <IdleModal
        translate={idleModalTranslate}
        isVisible={vm.showIdleModal}
        remainTime={vm.counter}
        resetIdle={vm.closeIdleModal}
      />
      {/* Google Pay browser fingerprint iframe — required by the payment gateway
          between submitGooglePayment and payment_confirmation. */}
      {vm.googlePayPaymentFingerprint && (
        <iframe
          allow="payment"
          className={toolboxStyles.fingerprintIframe}
          onLoad={vm.onGooglePayPaymentFingerprintLoaded}
          src={vm.googlePayPaymentFingerprint}
        />
      )}
    </div>
  );
});

registerView(PaymentRetryView, PaymentRetryViewModel);
